import React from "react";
import type { ProcessingMode } from "../lib/cleanseSchema";
import { PreviewTable } from "./PreviewTable";
import { ResultsTable } from "./ResultsTable";

export interface MainContentProps {
  mode: ProcessingMode;
  processing: boolean;
  paused: boolean;
  progress: { done: number; total: number };
  resultRows: any[];
  rows: Record<string, any>[];
  csvHeaders: string[];
  line1Col: string;
  postcodeCol: string;
  uprnCol: string;
  outputHeaders: string[];
  error: string | null;
  handleDownload: () => void;
  startNewCleanse: () => void;
  handleKeyDown: (e: React.KeyboardEvent, action: () => void) => void;
}

export function MainContent(props: MainContentProps) {
  const {
    mode,
    processing,
    paused,
    progress,
    resultRows,
    rows,
    csvHeaders,
    line1Col,
    postcodeCol,
    uprnCol,
    outputHeaders,
    error,
    handleDownload,
    startNewCleanse,
    handleKeyDown,
  } = props;

  return (
    <main className="flex-1 w-full px-6 py-6 max-w-7xl mx-auto">
      <section className="col-span-12 card p-6 shadow-lg border border-slate-700/50 backdrop-blur-sm">
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-bold text-slate-100">
              {processing
                ? mode === "cleanse"
                  ? "Cleansing in Progress"
                  : "UPRN Lookup in Progress"
                : "Data Preview"}
            </h2>
            {resultRows.length > 0 && !processing && (
              <div className="flex items-center gap-2">
                <button
                  className="px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-500 text-white text-sm focus:outline-hidden focus:ring-2 focus:ring-green-400"
                  onClick={handleDownload}
                  onKeyDown={(e) => handleKeyDown(e, handleDownload)}
                  aria-label="Download processed results as CSV file"
                >
                  {resultRows.length === rows.length
                    ? "Download CSV"
                    : `Download Partial Results (${resultRows.length}/${rows.length})`}
                </button>
                {resultRows.length === rows.length && (
                  <button
                    className="px-3 py-1.5 rounded-lg border-2 border-green-600 hover:border-green-500 hover:bg-green-600/10 text-green-400 hover:text-green-300 text-sm focus:outline-hidden focus:ring-2 focus:ring-green-400"
                    onClick={startNewCleanse}
                    onKeyDown={(e) => handleKeyDown(e, startNewCleanse)}
                    aria-label="Start cleansing a new CSV file"
                  >
                    Start New Cleanse
                  </button>
                )}
              </div>
            )}
          </div>
          <p className="text-sm text-slate-400">
            {rows.length > 0 && resultRows.length === 0 && !processing
              ? "Review your CSV data and verify column mappings before processing."
              : resultRows.length > 0 && resultRows.length === rows.length
                ? mode === "cleanse"
                  ? "Your processed addresses with cleansed data from the Ideal Postcodes API are ready."
                  : "Your UPRN lookups with full address details from the Ideal Postcodes API are ready."
                : resultRows.length > 0 && resultRows.length < rows.length
                  ? `Partial results available (${resultRows.length}/${rows.length} processed). You can download these results even if processing was interrupted.`
                  : processing
                    ? mode === "cleanse"
                      ? "Address cleansing in progress."
                      : "UPRN lookup in progress."
                    : mode === "cleanse"
                      ? "Upload a CSV file to start cleansing. Your results will appear in this preview."
                      : "Upload a CSV file with UPRN values to start lookup. Your results will appear in this preview."}
          </p>

          {resultRows.length > 0 && !processing && (
            <>
              {resultRows.length < rows.length && (
                <div className="mt-2 p-3 bg-amber-900/20 border border-amber-500/30 rounded-lg">
                  <div className="flex items-center gap-2">
                    <svg
                      className="w-4 h-4 text-amber-400 shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-sm text-amber-200">
                      <strong>Partial Results:</strong> Processing was
                      interrupted, but you can still download the{" "}
                      {resultRows.length} successfully processed addresses.
                    </span>
                  </div>
                </div>
              )}
              <div className="mt-2 p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                <div className="flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-blue-400 shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-sm text-blue-200">
                    New to address cleansing?{" "}
                    <a
                      href="https://ideal-postcodes.co.uk/guides/address-cleanse"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300 underline font-medium"
                    >
                      Learn what your results mean
                    </a>
                  </span>
                </div>
              </div>
            </>
          )}
        </div>

        {(processing || progress.total > 0) && (
          <div className="mb-6 p-4 bg-slate-800/50 rounded-lg border border-slate-700/30">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div
                  className={`w-3 h-3 rounded-full ${
                    processing && !paused
                      ? "bg-yellow-400 animate-pulse"
                      : error
                        ? "bg-red-400"
                        : progress.done > 0 && progress.done === progress.total
                          ? "bg-green-400"
                          : "bg-slate-400"
                  }`}
                ></div>
                <span
                  className={`text-sm font-medium ${
                    processing && !paused
                      ? "text-yellow-300"
                      : error
                        ? "text-red-300"
                        : progress.done > 0 && progress.done === progress.total
                          ? "text-green-300"
                          : "text-slate-300"
                  }`}
                >
                  {processing && !paused
                    ? "Processing..."
                    : error
                      ? "Error occurred"
                      : progress.done > 0 && progress.done === progress.total
                        ? "Completed"
                        : "Ready"}
                </span>
              </div>
              <div className="flex items-center gap-4">
                <span
                  className={`text-sm ${
                    processing && !paused
                      ? "text-yellow-300"
                      : error
                        ? "text-red-300"
                        : progress.done > 0
                          ? "text-green-300"
                          : "text-slate-300"
                  }`}
                >
                  {progress.done}/{progress.total}
                </span>
                <span
                  className={`text-lg font-bold ${
                    processing && !paused
                      ? "text-yellow-300"
                      : error
                        ? "text-red-300"
                        : progress.done > 0
                          ? "text-green-300"
                          : "text-slate-300"
                  }`}
                >
                  {progress.total > 0
                    ? ((progress.done / progress.total) * 100).toFixed(1)
                    : 0}
                  %
                </span>
              </div>
            </div>
            <div className="w-full bg-slate-700 rounded-full h-3">
              <div
                className={`h-3 rounded-full transition-all duration-500 ${
                  processing && !paused
                    ? "bg-yellow-400"
                    : error
                      ? "bg-red-400"
                      : progress.done > 0
                        ? "bg-green-400"
                        : "bg-slate-400"
                }`}
                style={{
                  width: `${
                    progress.total > 0
                      ? (progress.done / progress.total) * 100
                      : 0
                  }%`,
                }}
              ></div>
            </div>
          </div>
        )}

        {resultRows.length === 0 ? (
          rows.length > 0 &&
          !processing && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-medium text-slate-200">
                  Data Preview
                </h3>
                <div className="flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-green-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-sm text-green-300 font-medium">
                    Successfully loaded {rows.length} rows
                  </span>
                </div>
              </div>
              <PreviewTable
                rows={rows}
                csvHeaders={csvHeaders}
                mode={mode}
                line1Col={line1Col}
                postcodeCol={postcodeCol}
                uprnCol={uprnCol}
              />
              {rows.length > 10 && (
                <p className="text-sm text-slate-500 text-center">
                  Showing first 5 rows of {rows.length} total rows
                </p>
              )}
            </div>
          )
        ) : (
          <ResultsTable resultRows={resultRows} outputHeaders={outputHeaders} />
        )}
      </section>
    </main>
  );
}
